import {observer} from 'mobx-react';
import classNames from 'classnames';
import {Icon, PriorityIcon, DropdownV2} from '@priority-software/priority-style-react';
import React, {Component} from 'react';
import {formsStore} from '../../Form/stores/FormsStore';
import {isRTL} from '../../../Localization';
import {getHeaderFontSize} from '../../../_shared/GWTBridge/invoke/formGrid';
import {setIsReactPopupOpen} from '../../../_shared/GWTBridge/invoke/netForm';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {createFilter, createNewSearchAndExecute} from '../actions/filters';
import {showShouldExitMessage} from '../../../_shared/Messages/actions/MessageActions';
import {sendMonitor} from '../../../utils/monitoring';
import {iSearchDropdownCloseKey} from '../../FieldSearch/utils/keys';
import {CHANGE_IS_PAINTED, EXIT_FORM} from '../../FormHeader/consts/headerConsts';
import {getTopDialogZindex} from '../../../_shared/GWTBridge/invoke/priModalDlg';
import {FilterDropdownContent} from './FilterDropdownContent';
import {dropdownRef} from './DropdownRef';
import {FilterIconTrigger} from './FilterIconTrigger';

const defaultState = {
    tableName: undefined,
    field: undefined,
    title: undefined,
    headerDiv: undefined,
    fontSize: undefined,
    isOpen: false
};

@observer
export class FiltersDropdownComp extends Component<any, any> {
    filter;
    form;

    constructor(props) {
        super(props);
        this.state = defaultState;
    }

    open(tableName: string, field: string, title: string, headerDiv, fontSize) {
        this.setState({tableName, field, title, headerDiv, fontSize, isOpen: true});
        setIsReactPopupOpen(true);
    }

    updatePosition(tableName, field, headerDiv) {
        if (field === this.state.field && tableName === this.state.tableName) {
            this.setState({headerDiv});
        }
    }

    handleClearAllTags = () => {
        //"this.filter.value !== undefined" insted of this.filter.value  is for when its a bool field that equal to false
        this.filter.value !== undefined && this.filter.setValue(undefined);
        this.filter.values && this.filter.setValues([]);
    };

    handleSaveAndExecuteSearch = () => {
        createNewSearchAndExecute(this.form, this.filter);
        this.closeModal();
    };

    closeModal = () => {
        this.setState(defaultState);
        setIsReactPopupOpen(false);
        returnFocus();
    };

    //closeDropdown is the close function that comes from the Dropdown
    onClose = e => {
        if (this.filter.isDirty && ![CHANGE_IS_PAINTED, EXIT_FORM].includes(e.target.role)) {
            showShouldExitMessage(() => {
                //closeDropdown();
                this.closeModal();
            });
        } else {
            this.closeModal();
        }
    };

    render() {
        if (this.state.field) {
            const {tableName, field, title, headerDiv, fontSize} = this.state;
            this.form = formsStore.getForm(tableName);
            this.filter = createFilter(field, this.form);
            const isRTLDir = isRTL();
            const openingSide = isRTLDir
                ? headerDiv.right > 600
                    ? 'rightSide'
                    : 'leftSide'
                : headerDiv.left + 600 < window.innerWidth
                ? 'rightSide'
                : 'leftSide';

            return (
                <DropdownV2
                    oldStyles
                    componentClasses="header-icon"
                    style={{
                        top: headerDiv.top,
                        left: headerDiv.left,
                        right: 'auto',
                        zIndex: getTopDialogZindex()
                    }}
                    onClose={this.onClose}
                    isOpen={this.state.isOpen}
                    openingSide={openingSide}
                    contentStyle={{width: 600}}
                    trigger={
                        <FilterIconTrigger
                            title={title}
                            icon={PriorityIcon.filterFill}
                            skin="light"
                            width={headerDiv.width}
                            fontSize={fontSize}
                            isOpen={this.state.isOpen}
                            onClose={this.onClose}
                        />
                    }
                    isCloseKeyCallback={iSearchDropdownCloseKey}
                >
                    <FilterDropdownContent
                        onSaveSearch={this.handleSaveAndExecuteSearch}
                        clearAlltags={this.handleClearAllTags}
                        filter={this.filter}
                        search={this.form?.searches?.formSelected}
                    />
                </DropdownV2>
            );
        }
        return null;
    }
}

type FilterIconProps = {
    tableName: string;
    field: string;
    fieldName: string;
    title?: string;
    componentClasses?: string;
    session?: string;
    offsetParent?: Element;
    setCurrentForm?: () => void;
};

@observer
export class FilterIcon extends Component<FilterIconProps, null> {
    ref;

    constructor(props: FilterIconProps) {
        super(props);
        this.ref = React.createRef();
    }

    onClick = e => {
        e.stopPropagation();
        const {tableName, field, title, setCurrentForm} = this.props;
        if (setCurrentForm) {
            setCurrentForm();
        }

        const headerDiv = this.getParentNode().getBoundingClientRect();
        dropdownRef.current.open(tableName, field, title, headerDiv, getHeaderFontSize());

        const {currentForm} = formsStore;
        const fieldObj = currentForm.columnsMap.get(field);
        sendMonitor('OpenFilter', null, fieldObj && fieldObj.name);
    };

    componentDidUpdate(prevProps) {
        if (prevProps.field !== this.props.field) {
            const {tableName, field} = this.props;
            const parent = this.getParentNode();

            if (parent && dropdownRef.current) {
                const headerDiv = parent.getBoundingClientRect();
                dropdownRef.current.updatePosition(tableName, field, headerDiv);
            }
        }
    }

    getParentNode = () => this.props.offsetParent || this.ref.current?.offsetParent;

    isActive = () => {
        const {fieldName, tableName, session} = this.props;
        const form = session
            ? formsStore.getFormFromSession(session, tableName)
            : formsStore.getForm(tableName);
        const searchesStore = form?.searches;
        const filter = searchesStore?.formSelected?.filters.get(`${fieldName}`);

        return filter && !filter.isEmpty;
    };

    render() {
        const fontSize = getHeaderFontSize();
        const isActive = this.isActive();
        const {componentClasses} = this.props;

        const iconClasses = classNames('filter-icon', {
            filtered: isActive,
            componentClasses
        });

        return (
            <div ref={this.ref} className={iconClasses} style={{height: fontSize + 'pt'}}>
                <Icon
                    componentClasses="trigger-icon ignore-react-onclickoutside"
                    onClick={this.onClick}
                    icon={PriorityIcon.filterFill}
                    skin={isActive ? undefined : 'light'}
                    iconColor={isActive ? 'primary-500' : undefined}
                    style={{
                        marginTop: '-7.5pt',
                        width: fontSize + 7.5 + 'pt',
                        height: fontSize + 7.5 + 'pt',
                        fontSize: fontSize + 7.5 + 'pt'
                    }}
                />
            </div>
        );
    }
}
